export interface PagePerformance {
  pageName: string;
  loadTime: number;
  domContentLoaded: number;
  firstPaint?: number;
  firstContentfulPaint?: number;
}

function reportToServer(perf: PagePerformance) {
  try {
    navigator.sendBeacon(
      "/api/admin/metrics/client",
      new Blob([JSON.stringify(perf)], { type: "application/json" })
    );
  } catch {
    // Silently ignore — reporting failures must never affect the user experience
  }
}

export function trackPageLoad(pageName?: string): PagePerformance | null {
  if (typeof window === "undefined" || !window.performance) return null;

  const measureLoad = () => {
    const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const entry = entries[0];
    if (!entry) return null;

    const paintEntries = performance.getEntriesByType("paint");

    const loadTime = entry.loadEventEnd - entry.startTime;

    // loadEventEnd is 0 when the load event hasn't fully completed yet;
    // skip measurement rather than report invalid data.
    if (loadTime <= 0) return null;

    const perf: PagePerformance = {
      pageName: pageName || window.location.pathname,
      loadTime,
      domContentLoaded: entry.domContentLoadedEventEnd - entry.startTime,
      firstPaint: paintEntries.find((p) => p.name === "first-paint")?.startTime,
      firstContentfulPaint: paintEntries.find((p) => p.name === "first-contentful-paint")?.startTime,
    };

    const SLOW_PAGE_LOAD_THRESHOLD = 3000;
    if (perf.loadTime > SLOW_PAGE_LOAD_THRESHOLD) {
      console.warn(
        `[PERFORMANCE] Slow Page Load: ${perf.pageName} took ${perf.loadTime.toFixed(2)}ms`
      );
    }

    reportToServer(perf);

    return perf;
  };

  if (document.readyState === "complete") {
    return measureLoad();
  }

  window.addEventListener("load", () => {
    measureLoad();
  });

  return null;
}

export function trackRender<T>(componentName: string, renderFn: () => T): T {
  const start = performance.now();
  const result = renderFn();
  const duration = performance.now() - start;

  if (duration > 100) {
    console.warn(
      `[PERFORMANCE] Slow render: ${componentName} took ${duration.toFixed(2)}ms`
    );
  }

  return result;
}

export interface WebVitalsPayload {
  name: string;
  value: number;
  id: string;
  page: string;
}

export interface ErrorPayload {
  message: string;
  stack?: string;
  page: string;
  componentStack?: string;
}

function sendTelemetry(endpoint: string, payload: unknown): void {
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, new Blob([JSON.stringify(payload)], { type: "application/json" }));
    } else {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {}
}

export function reportWebVital(vital: WebVitalsPayload): void {
  // Legacy ingest (kept for back-compat dashboards).
  sendTelemetry("/api/telemetry/vitals", vital);
  // Task #1959 — Observability gold: also fan out to the RUM histogram so
  // `nashid_web_vitals_seconds` is populated with real browser data. The
  // server clamps and normalises units (CLS unitless, others ms→s).
  const rating =
    vital.name === "CLS"
      ? vital.value < 0.1 ? "good" : vital.value < 0.25 ? "needs-improvement" : "poor"
      : vital.name === "LCP"
        ? vital.value < 2500 ? "good" : vital.value < 4000 ? "needs-improvement" : "poor"
        : vital.name === "FID" || vital.name === "INP"
          ? vital.value < 100 ? "good" : vital.value < 300 ? "needs-improvement" : "poor"
          : vital.name === "TTFB"
            ? vital.value < 800 ? "good" : vital.value < 1800 ? "needs-improvement" : "poor"
            : "unknown";
  sendTelemetry("/api/rum/web-vitals", { ...vital, rating });
  import("./otel").then(({ recordWebVitalSpan }) => {
    recordWebVitalSpan({
      name: vital.name,
      value: vital.value,
      id: vital.id,
      page: vital.page,
    });
  }).catch(() => {});
}

export function reportError(payload: ErrorPayload): void {
  sendTelemetry("/api/telemetry/errors", payload);
  import("./otel").then(({ recordFrontendErrorSpan }) => {
    recordFrontendErrorSpan({
      message: payload.message,
      stack: payload.stack,
      page: payload.page,
      componentStack: payload.componentStack,
    });
  }).catch(() => {});
}

export function initWebVitals(): void {
  if (typeof window === "undefined") return;

  try {
    const observeLCP = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) {
          reportWebVital({ name: "LCP", value: last.startTime, id: "lcp-" + Date.now(), page: window.location.pathname });
        }
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
    };

    const observeCLS = () => {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const e = entry as any;
          if (!e.hadRecentInput) {
            clsValue += e.value;
          }
        }
        reportWebVital({ name: "CLS", value: clsValue, id: "cls-" + Date.now(), page: window.location.pathname });
      });
      observer.observe({ type: "layout-shift", buffered: true });
    };

    const observeFID = () => {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const first = entries[0] as any;
        if (first) {
          reportWebVital({ name: "FID", value: first.processingStart - first.startTime, id: "fid-" + Date.now(), page: window.location.pathname });
        }
      });
      observer.observe({ type: "first-input", buffered: true });
    };

    const observeTTFB = () => {
      const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
      const nav = entries[0];
      if (nav) {
        reportWebVital({ name: "TTFB", value: nav.responseStart - nav.requestStart, id: "ttfb-" + Date.now(), page: window.location.pathname });
      }
    };

    if (typeof PerformanceObserver !== "undefined") {
      try { observeLCP(); } catch (_) {}
      try { observeCLS(); } catch (_) {}
      try { observeFID(); } catch (_) {}
    }

    if (document.readyState === "complete") {
      observeTTFB();
    } else {
      window.addEventListener("load", observeTTFB, { once: true });
    }
  } catch (_) {}
}

// ─── SPA Navigation Timing (Task #3408 Step 13) ──────────────────────────────
// Lightweight soft-navigation tracker that measures the interval from the first
// location change (topProgressStore.start) to when the new page commits
// (RouteTracker calls topProgressStore.done). The measurement is fanned out to
// the RUM /api/rum/web-vitals endpoint using the synthetic metric name "NAV".

interface NavEntry {
  from: string;
  startTime: number;
}

let _pendingNav: NavEntry | null = null;

/**
 * Call when a SPA route transition begins (location just changed).
 * @param fromPath  the pathname being navigated away from
 */
export function startNavigation(fromPath: string): void {
  _pendingNav = { from: fromPath, startTime: performance.now() };
}

/**
 * Call when the new route's component has finished committing to the DOM.
 * @param toPath  the pathname that just finished rendering
 */
export function completeNavigation(toPath: string): void {
  if (!_pendingNav) return;
  const durationMs = performance.now() - _pendingNav.startTime;
  const from = _pendingNav.from;
  _pendingNav = null;

  const NAV_SLOW_THRESHOLD_MS = 1000;
  if (durationMs > NAV_SLOW_THRESHOLD_MS) {
    console.debug(`[PERFORMANCE] Slow navigation: ${from} → ${toPath} took ${durationMs.toFixed(0)}ms`);
  }

  // Fan out to RUM using the same Web-Vitals endpoint so existing dashboards
  // can chart navigation latency alongside LCP/CLS/FID.
  sendTelemetry("/api/rum/web-vitals", {
    name: "NAV",
    value: Math.round(durationMs),
    id: `nav-${Date.now()}`,
    page: toPath,
    from,
    rating: durationMs < 300 ? "good" : durationMs < 1000 ? "needs-improvement" : "poor",
  });
}

export function initUnhandledErrorReporting(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    reportError({
      message: event.message,
      stack: event.error?.stack,
      page: window.location.pathname,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const message = event.reason?.message || String(event.reason) || "Unhandled Promise Rejection";
    reportError({
      message,
      stack: event.reason?.stack,
      page: window.location.pathname,
    });
  });
}
